import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from '@angular/router';
import {AuthStore} from '../../+store/auth.store';
import {PermissionType, UserRoleType} from '@core/api/data-access';
import {ActiveUserStore} from '@active-user-management';

type PermissionsGuardOptions = {
	fallbackRoute: string[] | string;
	fallbackLogout: boolean;
};

const defaultOptions: PermissionsGuardOptions = {
	fallbackRoute: ['login'],
	fallbackLogout: false,
};

export function PermissionsGuard(allowedPermissions: (PermissionType | UserRoleType)[], options: Partial<PermissionsGuardOptions> = defaultOptions): CanActivateFn {
	const {fallbackRoute, fallbackLogout} = {...defaultOptions, ...options};
	return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
		const authStore: AuthStore = inject(AuthStore);
		const activeUserStore = inject(ActiveUserStore);
		const router: Router = inject(Router);

		const _activeUserPermissions: PermissionType[] = authStore.activeUserPermissions$();
		const _activeUserRoles: UserRoleType[] = activeUserStore.user$().roles || [];
		const activeUserPermissionsAndRoles: (PermissionType | UserRoleType)[] = [..._activeUserPermissions, ..._activeUserRoles];
		const isAllowed: boolean = allowedPermissions.some((permission: PermissionType | UserRoleType) => activeUserPermissionsAndRoles.includes(permission));
		if (!isAllowed) {
			let navTo: string;
			if (fallbackRoute) navTo = Array.isArray(fallbackRoute) ? fallbackRoute.join('/') : fallbackRoute;
			if (fallbackLogout) {
				console.debug('PermissionsGuard: User does not have permissions to access this route. Logging out...', {
					allowedPermissions,
					activeUserPermissionsAndRoles,
					route,
					state,
				});
				authStore.logout(navTo);
			}
			if (route) {
				console.debug('PermissionsGuard: User does not have permissions to access this route. Redirecting to fallback route...', {
					allowedPermissions,
					activeUserPermissionsAndRoles,
					route,
					state,
					navTo,
				});
				router.navigate([navTo]).then();
			}
		}
		return isAllowed;
	};
}
